"use client"

import { FormEvent, useState } from "react"
import { Alert } from "src/components/common/Alert"
import { GenericErrorMessage } from "src/components/common/GenericErrorMessage"
import { Input } from "src/components/common/Input"
import { PrimaryButton } from "src/components/common/PrimaryButton"
import { BellRingIcon, StepCompleteIcon } from "src/components/icons"
import { action_subscribeToBackInStockNotification } from "src/components/product/actions"
import { A } from "src/components/unstyled/A"
import { AnimateHeight } from "src/components/unstyled/AnimateHeight"
import { routes } from "src/config/routes"
import { contactStore, mergeContactData } from "src/integrations/lib/state"
import { localizeText } from "src/localization/utils"
import { recordError } from "src/utilities/errorReporting"

export type SubscribeToBackInStockProps = {
  sku: string
}

export const SubscribeToBackInStock = ({ sku }: SubscribeToBackInStockProps) => {
  const [opened, setOpened] = useState(false)
  const email = contactStore((state) => state.email)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(false)

  function handleEmailChange(email: string) {
    mergeContactData({ email })
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()

    try {
      setLoading(true)
      setSuccess(false)
      setError(false)

      const result = await action_subscribeToBackInStockNotification({ sku, email: email || "" })

      if (!result.ok) {
        throw new Error(result.status + " " + result.statusText + ": " + result.body)
      }

      setSuccess(true)
    } catch (error) {
      recordError("Failed to subscribe to back in stock notifications for SKU: " + sku, error)
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  const T = localizeText(text)

  return (
    <Alert>
      <AnimateHeight expanded={opened} collapsedHeight="auto">
        {success ? (
          <div className="flex items-center gap-4">
            <StepCompleteIcon className="h-6 w-6 shrink-0" />
            {T.youAreSignedUp}
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-4">
              <BellRingIcon className="h-6 w-6 shrink-0" /> {T.getNotified}
            </div>

            {!opened ? (
              <PrimaryButton
                type="button"
                size="small"
                color="primary"
                loading={loading}
                onClick={() => setOpened(true)}
              >
                {T.notifyMe}
              </PrimaryButton>
            ) : (
              <form onSubmit={handleSubmit} inert={loading} className="flex flex-col items-stretch gap-6">
                <Input
                  type="email"
                  name="email"
                  autoComplete="home email"
                  label="E-Mail"
                  required
                  large
                  value={email}
                  onChange={(event) => handleEmailChange(event.target.value.trim())}
                />

                <div className="text-sm">{T.youAcceptTheTerms}</div>

                {error && <GenericErrorMessage />}

                <PrimaryButton type="submit" size="small" color="primary" loading={loading}>
                  {T.signUp}
                </PrimaryButton>
              </form>
            )}
          </div>
        )}
      </AnimateHeight>
    </Alert>
  )
}

const de = {
  getNotified: "Werde benachrichtigt, wenn die Box wieder verfügbar ist:",
  youAcceptTheTerms: (
    <>
      Mit der Anmeldung akzeptierst du die{" "}
      <A href={routes.privacy} className="whitespace-nowrap underline" target="_blank">
        Datenschutzbestimmungen
      </A>
      .
    </>
  ),
  notifyMe: "Info erhalten",
  signUp: "Anmelden",
  youAreSignedUp: "Du bist angemeldet und wirst benachrichtigt, sobald die Box wieder verfügbar ist.",
}

const en: typeof de = {
  getNotified: "Get notified when the product is back in stock:",
  youAcceptTheTerms: (
    <>
      By signing up you accept the{" "}
      <A href={routes.privacy} className="whitespace-nowrap underline" target="_blank">
        privacy policy
      </A>
      .
    </>
  ),
  notifyMe: "Notify me",
  signUp: "Sign up",
  youAreSignedUp: "You are signed up and will be notified when the product is back in stock.",
}

const text = { de, en }
